import { createContext, useContext } from 'react';
import { Theme } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { HeatmapColorSettings } from '../../theme';
import { TraceConfig, TraceGreek, ZoomData } from '../../types';
import { TraceShowChartType } from '../../config/oi';
import type { TraceBreakpoint } from '../../hooks/trace/useTraceSize';

export type TraceMode = 'standalone' | 'canvas';

export interface TraceContextValue {
  // Controlled config (URL-backed on standalone; Canvas-backed in a tile)
  config: TraceConfig;
  updateConfig: (patch: Partial<TraceConfig>) => void;

  // Entitlements
  userTraceV2Enabled: boolean;
  // Internal users get the VIX sym option.
  isInternalUser: boolean;
  // Expose the internal-only lense options like `GAMMA_COLOR`. Only true on internal tab
  showInternalLenses: boolean;
  // Where this Trace is mounted. Used to suppress Canvas-redundant chrome
  // like the fullscreen button (Canvas tiles have their own expand control).
  mode: TraceMode;

  // Container size & 3-tier breakpoint — measured via ResizeObserver on the
  // TraceProvider root. Consumers use this (not viewport breakpoints) to
  // decide layout, since Canvas tiles can be much smaller than the viewport
  // and the viewport's height oscillates on mobile when the address bar
  // shows / hides.
  containerWidth: number;
  containerHeight: number;
  breakpoint: TraceBreakpoint;
  // Convenience flags derived from `breakpoint`. Use these instead of the
  // viewport-based useIsMobile() so layout only reacts to real container
  // changes, not to viewport jitter.
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Derived, memoized values sourced from config + theme + Recoil.
  intradayDate: Dayjs;
  intradaySym: string;
  selectedGreek: TraceGreek;
  selectedLense: TraceConfig['lense'];
  selectedTheme: Theme;
  heatmapColorSettings: HeatmapColorSettings;

  // Ephemeral UI state shared across Trace subcomponents
  fullscreen: boolean;
  setFullscreen: (v: boolean) => void;
  showChartType: TraceShowChartType;
  setShowChartType: (v: TraceShowChartType) => void;
  zoomData: ZoomData | undefined;
  setZoomData: (v: ZoomData | undefined) => void;
}

export const TraceContext = createContext<TraceContextValue | null>(null);

export const useTraceCtx = (): TraceContextValue => {
  const ctx = useContext(TraceContext);
  if (ctx == null) {
    throw new Error('useTraceCtx must be used within a TraceProvider');
  }
  return ctx;
};
